/* global axios */
import ApiClient from './ApiClient';

// Client da API unificada de Automações (fluxos). Cobre o CRUD do draft mais as
// ações próprias da feature: publish (gera a versão executável), run (execução
// manual — a aba de macros da conversa usa isto), duplicate e catalog (paleta).
class AutomationFlowsAPI extends ApiClient {
  constructor() {
    super('automation_flows', { accountScoped: true });
  }

  // Lista fluxos, opcionalmente filtrando por gatilho. A aba de macros da
  // conversa passa triggerType: 'manual' + published: true — só o executável.
  get({
    triggerType,
    page,
    perPage,
    searchKey,
    sortOrder,
    active,
    published,
    status,
    directoryId,
    labelIds,
  } = {}) {
    return axios.get(this.url, {
      params: {
        trigger_type: triggerType,
        // Pasta (#1071). `'none'` e a RAIZ (fluxo sem pasta), nao "sem filtro" —
        // sem essa forma nao haveria como ver o que ainda nao foi organizado.
        // O explorador de arquivos da #1197 manda o id da pasta ABERTA aqui, e
        // `'none'` ao estar na raiz.
        directory_id: directoryId,
        // Etiquetas por ID, separadas por virgula; o fluxo casa com QUALQUER
        // uma. Era por TITULO (`labels=`) ate a #1197: com catalogo proprio o id
        // e estavel e o titulo e editavel, entao filtrar por texto faria o
        // filtro parar de valer no dia em que alguem renomeasse a etiqueta.
        label_ids: labelIds,
        page,
        // Sem valor aqui o backend usa 15, a página da tela de fluxos. Os
        // seletores pedem 100 (teto do controller) porque precisam da lista
        // inteira — ver fetchAllForSelect no store.
        per_page: perPage,
        q: searchKey,
        sort_order: sortOrder,
        active,
        published,
        status,
      },
    });
  }

  // Todos os fluxos, não só a primeira página.
  //
  // Os seletores (bot, bloco executar fluxo, ação de macro/automação, campanha)
  // pediam a lista sem página e ficavam com os 15 primeiros por ordem
  // alfabética. Numa conta com centenas de fluxos, o que o usuário procurava
  // simplesmente não estava lá — e não havia como perceber, porque a lista não
  // se diz truncada (issue #663).
  //
  // A parada é pelo TAMANHO DA PÁGINA, não pelo `count` do servidor: página
  // menor que `perPage` é a última, e ponto. Terminar pelo `count` parecia mais
  // direto, mas um count menor que o total real truncaria a lista em silêncio —
  // que é exatamente o defeito desta issue, reintroduzido por outro caminho.
  //
  // `maxPages` é rede de segurança contra laço infinito se o backend ignorar a
  // paginação e devolver sempre a mesma página cheia. 50 × 100 = 5000 fluxos,
  // ordens de grandeza acima da conta mais carregada que temos.
  async getAll({ perPage = 100, maxPages = 50, ...params } = {}) {
    const registros = [];

    for (let pagina = 1; pagina <= maxPages; pagina += 1) {
      // eslint-disable-next-line no-await-in-loop
      const { data } = await this.get({ ...params, page: pagina, perPage });
      const atual = data.payload || [];
      registros.push(...atual);
      if (atual.length < perPage) break;
    }

    return registros;
  }

  publish(id) {
    return axios.post(`${this.url}/${id}/publish`);
  }

  // Roda o RASCUNHO numa conversa de teste e devolve para onde navegar:
  // { conversation_id, inbox_id, session_id }.
  testRun({ id, mode = 'continue' }) {
    return axios.post(`${this.url}/${id}/test_run`, { mode });
  }

  // Execução manual; devolve { session_ids } (fim do fire-and-forget).
  // `variables` = valores do formulário de variáveis de entrada (nome=>valor);
  // o backend só aceita as chaves declaradas na versão publicada.
  run({ id, conversationIds, body = {}, variables = {} }) {
    return axios.post(`${this.url}/${id}/run`, {
      conversation_ids: conversationIds,
      body,
      variables,
    });
  }

  // Uma execução específica — status/output/variables (o indicador da
  // conversa faz polling daqui até completar/falhar/pausar).
  session(id, sessionId) {
    return axios.get(`${this.url}/${id}/sessions/${sessionId}`);
  }

  duplicate(id) {
    return axios.post(`${this.url}/${id}/duplicate`);
  }

  // Execuções do fluxo (mais recentes primeiro, 20 por página) — a aba de
  // Execuções do editor. Devolve { meta, payload } como o index.
  sessions(id, { page } = {}) {
    return axios.get(`${this.url}/${id}/sessions`, { params: { page } });
  }

  // Versões PUBLICADAS do fluxo (mais nova primeiro, 20 por página) — a aba
  // Versões do editor (#1705). A de teste não entra. Devolve { meta, payload }.
  versions(id, { page } = {}) {
    return axios.get(`${this.url}/${id}/versions`, { params: { page } });
  }

  // UMA versão publicada COM o grafo (`definition`) — a visualização somente
  // leitura (#1705). Versão de teste, de outro fluxo ou de outra conta é 404.
  version(id, versionId) {
    return axios.get(`${this.url}/${id}/versions/${versionId}`);
  }

  catalog() {
    return axios.get(`${this.url}/catalog`);
  }
}

export default new AutomationFlowsAPI();
